import { Box, Tab, Tabs } from '@mui/material';
import React from 'react';
import './index.scss';

export interface CRMTabItem {
  label: string;
  /** MUI's Tab accepts only a string or a single element here, not the wider ReactNode. */
  icon?: React.ReactElement | string;
}

interface CRMTabsProps {
  value: number;
  onChange: (newValue: number) => void;
  tabs: CRMTabItem[];
  /** Pin the tab bar to the top of its scroll container (default true). Ignored when inline. */
  sticky?: boolean;
  /**
   * `inline` is a selector sitting in a section header (a 7/30/90 days period switch): never
   * sticky, no margin of its own, control height. It exists so the two settings that go together
   * cannot be half-applied: a sticky selector inside a card pins at the same spot as the page's own
   * tabs and paints over them, which is the dashboard bug this variant was made for.
   */
  variant?: 'page' | 'inline';
  className?: string;
}

/**
 * Shared Control-Center tab switcher. Use this for every top-level tabbed page
 * so the tab look (font, casing, active colour, indicator) stays identical
 * across the CC. Styling lives in ./index.scss — never restyle tabs per page.
 */
const CRMTabs: React.FC<CRMTabsProps> = ({ value, onChange, tabs, sticky = true, variant = 'page', className }) => {
  const inline = variant === 'inline';
  const classes = [
    'crm-tab-bar',
    !inline && sticky ? 'crm-tab-bar--sticky' : '',
    inline ? 'crm-tab-bar--embedded crm-tab-bar--inline' : '',
    className ?? '',
  ].filter(Boolean).join(' ');
  return (
  <Box className={classes}>
    <Tabs
      value={value}
      onChange={(_, v) => onChange(v)}
      className="crm-tabs"
      variant="scrollable"
      /*
       * No allowScrollButtonsMobile. It forces MUI's arrows to render on touch devices, and the
       * left one takes 40px off the leading edge -- so on a phone the first tab began 40px in
       * from the page gutter, but only on the pages whose tabs overflow. /notifications,
       * /email-campaigns and /cloud-costs were indented; /companies and /applications, with two
       * tabs each, were not. The tab bar looked like it moved between pages.
       *
       * Without it MUI hides the buttons below sm and the strip scrolls by swipe, which is the
       * gesture a phone offers anyway. Desktop keeps the arrows, where there is no gesture and a
       * pointer to click them with.
       */
    >
      {tabs.map((t, i) => (
        <Tab
          key={i}
          icon={t.icon ?? undefined}
          iconPosition={t.icon ? 'start' : undefined}
          label={t.label}
        />
      ))}
    </Tabs>
  </Box>
  );
};

export default CRMTabs;
